<template>
  <q-banner
    v-if="isVisible"
    :role="bannerRole"
    :aria-live="ariaLive"
    :class="['banner', bannerClasses]"
    rounded
  >
    <div
      :class="['banner__container', {'banner__container__has-action': !!$slots.action}]"
    >
      <div
        :class="['banner__main', {'banner__main__has-action': !!$slots.action}]"
      >
        <div
          v-if="hasIcon"
          :class="['banner__icon-container', iconContainerClasses]"
        >
          <slot name="icon">
            <q-icon
              :name="iconName"
              color="white"
              size="sm"
              :role="iconLabel ? 'img' : null"
              :aria-label="iconLabel || null"
              :aria-hidden="!iconLabel"
            />
          </slot>
        </div>

        <div class="banner__content">
          <component
            :is="headingLevel"
            v-if="heading"
            class="banner__heading"
          >
            {{ heading }}
          </component>
          <div class="banner__message">
            <slot name="message">
              {{ message }}
            </slot>
          </div>
        </div>
      </div>
      <div
        v-if="$slots.action || dismissible"
        :class="['banner__aside', { 'banner__aside__has-action': !!$slots.action }]"
      >
        <div
          v-if="$slots.action"
          class="banner__action"
        >
          <slot name="action" />
        </div>

        <div
          v-if="dismissible"
          class="banner__close"
        >
          <q-btn
            flat
            round
            dense
            icon="close"
            :aria-label="closeLabel"
            @click="dismiss"
          />
        </div>
      </div>
    </div>
  </q-banner>
</template>

<script>
export default {
  name: 'Banner',

  props: {
    type: {
      type: String,
      default: 'info',
      validator: (value) => ['info', 'success', 'warning', 'error'].includes(value),
    },

    subtype: {
      type: String,
      default: 'general',
      validator: (value) => ['general', 'message', 'note', 'appointment'].includes(value),
    },

    heading: {
      type: String,
      default: null,
    },

    message: {
      type: String,
      default: null,
    },

    icon: {
      type: String,
      default: null,
    },

    iconLabel: {
      type: String,
      default: null,
    },

    showIcon: {
      type: Boolean,
      default: false,
    },

    // Heading level for semantic markup (h1-h6)
    headingLevel: {
      type: String,
      default: 'h2',
      validator: (value) => ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(value),
    },

    variant: {
      type: String,
      default: 'single-line',
      validator: (value) => ['single-line', 'multi-line'].includes(value),
    },

    dismissible: {
      type: Boolean,
      default: false,
    },

    closeLabel: {
      type: String,
      default: 'Dismiss banner',
    },
  },

  emits: ['dismiss'],

  data() {
    return {
      isVisible: true,
    };
  },

  computed: {
    bannerClasses() {
      return {
        [`banner--${this.type}`]: true,
        [`banner--${this.variant}`]: true,
      };
    },

    iconContainerClasses() {
      return `banner__icon-container--${this.type}`;
    },

    iconName() {
      if (this.icon) {
        return this.icon;
      }

      if (this.type === 'info') {
        const subtypeIconMap = {
          general: 'info',
          message: 'chat_bubble',
          note: 'event_note',
          appointment: 'event',
        };
        return subtypeIconMap[this.subtype] || 'info';
      }

      const iconMap = {
        info: 'info',
        success: 'check_circle',
        warning: 'warning',
        error: 'error',
      };

      return iconMap[this.type] || 'info';
    },

    hasIcon() {
      return !!this.$slots.icon
        || !!this.icon
        || this.showIcon
        || (this.type === 'info' && this.subtype !== 'general');
    },

    bannerRole() {
      return this.type === 'error' ? 'alert' : 'status';
    },

    ariaLive() {
      if (this.type === 'error') {
        return 'assertive';
      }

      return 'polite';
    },
  },

  methods: {
    show() {
      this.isVisible = true;
    },

    dismiss() {
      this.isVisible = false;
      this.$emit('dismiss');
    },
  },
};
</script>

<style scoped lang="scss">
.banner {
  width: 100%;
  padding: 16px 24px 16px 16px;
  box-sizing: border-box;

  &__container {
    display: flex;
    align-items: flex-start;
    gap: 16px;
  }

  &__main {
    display: flex;
    align-items: flex-start;
    gap: 16px;
    flex: 1;
    min-width: 0;
  }

  &__icon-container {
    flex-shrink: 0;
    width: 40px;
    height: 40px;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;

    &--info {
      background-color: $info-bg;
    }

    &--success {
      background-color: $success-bg;
    }

    &--warning {
      background-color: $warning-bg;
    }

    &--error {
      background-color: $negative-bg;
    }
  }

  &__content {
    flex: 1;
    min-width: 0;
  }

  &__heading {
    margin: 0;
    font-size: 0.875rem;
    font-weight: 400;
    line-height: 1.25rem;
  }

  &__message {
    margin: 0;
    font-size: 0.875rem;
    font-weight: 400;
    line-height: 1.25rem;
  }

  &__aside {
    display: flex;
    gap: 8px;
    flex-shrink: 0;
    align-items: flex-start;
  }

  &__action {
    display: flex;
    gap: 8px;
    align-items: center;

    :deep(.q-btn) {
      span {
        text-align: right !important;
      }
    }

    @media only screen and (max-width: $breakpoint-xs) {
      flex-wrap: wrap;
      justify-content: flex-end;
    }
  }

  &__close {
    display: flex;
    align-items: center;
    flex-shrink: 0;

    :deep(.q-btn) {
      min-width: 32px;
      min-height: 32px;
    }
  }

  &--info {
    background-color: $info-bg-light;
  }

  &--success {
    background-color: $success-bg-light;
  }

  &--warning {
    background-color: $warning-bg-light;
  }

  &--error {
    background-color: $negative-bg-light;
  }

  &--single-line {
    .banner__container {
      align-items: center;
    }

    .banner__container__has-action {
      @media only screen and (max-width: $breakpoint-sm) {
        flex-direction: column;
        align-items: stretch;
      }
    }

    .banner__main {
      align-items: center;
    }

    .banner__main__has-action {
      @media (max-width: $breakpoint-sm) {
        width: 100%;
        align-items: flex-start
      }
    }

    .banner__heading {
      display: inline;
    }

    .banner__message {
      display: inline;
    }

    .banner__content {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 4px;
    }

    .banner__aside {
      align-items: center;
    }

    .banner__aside__has-action {
      @media only screen and (max-width: $breakpoint-sm) {
        width: 100%;
        justify-content: flex-end;
      }
    }
  }

  &--multi-line {
    padding: 24px;

    .banner__container {
      flex-wrap: wrap;
    }

    .banner__main {
      flex: 1;
      min-width: 0;
    }

    .banner__aside {
      width: 100%;
      justify-content: flex-end;
    }
  }
}

@media (prefers-reduced-motion: reduce) {
  .banner {
    transition: none !important;
    animation: none !important;
  }
}
</style>
